import { Input, Select, Toggle } from "../shared";
import { COMPLIANCE_PROFILE_OPTIONS } from "../pages/ManifestsV2/utils";
import { HAZMAT_FIELD_LABELS } from "../utils/legShipping";

/**
 * Manifest V2 shipping / hazmat / compliance fields for leg create-edit.
 * Shown only when Build trip and Manifest V2 are both opted in.
 *
 * @param {object} props
 * @param {object} props.shipping - form shipping object
 * @param {function} props.onChange - (nextShipping) => void
 * @param {string} [props.complianceProfile] - optional leg-level override
 * @param {function} [props.onComplianceProfileChange]
 * @param {string} [props.size]
 */
export function LegShippingFields({
  shipping = {},
  onChange,
  complianceProfile = "",
  onComplianceProfileChange,
  size = "md",
}) {
  const hazmat = shipping.hazmat || {};
  const temperatureRange = shipping.temperatureRange || { min: "", max: "", unit: "F" };

  function updateShipping(partial) {
    onChange({ ...shipping, ...partial });
  }

  function updateHazmat(field, value) {
    onChange({
      ...shipping,
      hazmat: { ...hazmat, [field]: value },
    });
  }

  function updateTemperatureRange(field, value) {
    onChange({
      ...shipping,
      temperatureRange: { ...temperatureRange, [field]: value },
    });
  }

  return (
    <div className="mt-6 space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Shipping & compliance</h3>
        <p className="text-xs text-gray-500">
          Required for Manifest V2 dispatch and roadside packet readiness.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Input
          size={size}
          label="BOL number"
          value={shipping.bolNumber || ""}
          onChange={(e) => updateShipping({ bolNumber: e.target.value })}
        />
        <Input
          size={size}
          label="PRO number"
          value={shipping.proNumber || ""}
          onChange={(e) => updateShipping({ proNumber: e.target.value })}
        />
        <Input
          size={size}
          label="Commodity"
          value={shipping.commodity || ""}
          onChange={(e) => updateShipping({ commodity: e.target.value })}
        />
        <Input
          size={size}
          label="Pieces"
          type="number"
          value={shipping.pieces ?? ""}
          onChange={(e) => updateShipping({ pieces: e.target.value })}
        />
        <Input
          size={size}
          label="Shipper reference"
          value={shipping.shipperReference || ""}
          onChange={(e) => updateShipping({ shipperReference: e.target.value })}
        />
        <Input
          size={size}
          label="Consignee reference"
          value={shipping.consigneeReference || ""}
          onChange={(e) => updateShipping({ consigneeReference: e.target.value })}
        />
        <div className="md:col-span-2">
          <Input
            size={size}
            label="Customer reference numbers"
            placeholder="Comma-separated"
            value={shipping.customerReferenceNumbers || ""}
            onChange={(e) =>
              updateShipping({ customerReferenceNumbers: e.target.value })
            }
          />
        </div>
      </div>

      {onComplianceProfileChange ? (
        <Select
          size={size}
          label="Compliance profile override"
          placeholder="Use manifest default"
          options={[{ value: "", label: "Use manifest default" }, ...COMPLIANCE_PROFILE_OPTIONS]}
          value={complianceProfile || ""}
          onChange={(option) => onComplianceProfileChange(option?.value || "")}
        />
      ) : null}

      <Toggle
        label="Hazmat load"
        checked={Boolean(shipping.isHazmat)}
        onChange={(checked) => updateShipping({ isHazmat: checked })}
      />

      {shipping.isHazmat ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {Object.keys(HAZMAT_FIELD_LABELS).map((field) => (
            <Input
              key={field}
              size={size}
              label={HAZMAT_FIELD_LABELS[field]}
              value={hazmat[field] || ""}
              onChange={(e) => updateHazmat(field, e.target.value)}
            />
          ))}
        </div>
      ) : null}

      <Toggle
        label="Reefer temperature required"
        checked={Boolean(shipping.temperatureRequired)}
        onChange={(checked) => updateShipping({ temperatureRequired: checked })}
      />

      {shipping.temperatureRequired ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <Input
            size={size}
            label="Min temp"
            type="number"
            value={temperatureRange.min ?? ""}
            onChange={(e) => updateTemperatureRange("min", e.target.value)}
          />
          <Input
            size={size}
            label="Max temp"
            type="number"
            value={temperatureRange.max ?? ""}
            onChange={(e) => updateTemperatureRange("max", e.target.value)}
          />
          <Select
            size={size}
            label="Unit"
            options={[
              { value: "F", label: "°F" },
              { value: "C", label: "°C" },
            ]}
            value={temperatureRange.unit || "F"}
            onChange={(option) => updateTemperatureRange("unit", option?.value || "F")}
          />
        </div>
      ) : null}
    </div>
  );
}

export default LegShippingFields;
